import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useTooltip } from "context/TooltipProvider";
import {
  addAssetRecoveryReport,
  addReport,
  updateAssetReferralProhibitionRequest, // Import this for updating prohibition status
  updateAssetReferral,
  fetchAssetReferralById,
} from "services/assetRecoveryService";
import { MultipleFileUploadComponent } from "components/form/MultipleFileUpload";
import {
  Asset,
  AssetReferralActionStatusEnum,
  ProhibitionRequest,
  Referral,
  Report,
} from "types";
import { getMappingDisplayText } from "utils/constant";

interface FinalReportProps {
  referralId?: string;
  setActiveTab?: React.Dispatch<React.SetStateAction<string>>;
  onNewDataAdded?: (data: any) => void;
  referral: Referral | null;
  assets?: Array<{
    id: string;
    assets: Asset[];
    asset_suspects: any[];
    suspect_count: number;
  }>;
  reports?: Report[];
  recoveryReports?: Report[];
  assetsNeedingReport: Array<{
    id: string;
    assets: Asset[];
    asset_suspects: any[];
    suspect_count: number;
  }>;
  prohibitionRecord: ProhibitionRequest[] | null; // Renamed and made required
}

export const FinalReport: React.FC<FinalReportProps> = ({
  referralId,
  onNewDataAdded,
  setActiveTab,
  referral,
  assets = [],
  reports = [],
  recoveryReports = [],
  assetsNeedingReport,
  prohibitionRecord,
}) => {
  const [formData, setFormData] = useState<{
    prohibition_id: string; // Added prohibition_id
    court_decision_date: string;
    court_decision_summary: string;
    prosecutor_feedback_on_prohabition_decision: string;
    suspect_asset_mapping: string;
  }>({
    prohibition_id: "", // Initialize empty
    court_decision_date: "",
    court_decision_summary: "",
    prosecutor_feedback_on_prohabition_decision: "",
    suspect_asset_mapping: "",
  });

  const [submitting, setSubmitting] = useState(false);
  const { showTooltip } = useTooltip();
  const { t } = useTranslation();
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);

  // Filter prohibition records that are in litigation status (eligible for final reports)
  const eligibleProhibitionRecords =
    prohibitionRecord?.filter(
      (record) =>
        record.status === "Prohibition On litigation" ||
        record.status === "PROHIBITION_ON_LITIGATION",
    ) || [];

  // Get reports for the selected prohibition only
  const finalVerdictReports = (() => {
    if (!formData.prohibition_id) return [];
    return reports.filter(
      (r) =>
        r.is_next_appointment_reason_final_verdict &&
        r.prohabition_request === formData.prohibition_id,
    );
  })();

  const coveredSuspectAssetMappingIds = new Set<string>();
  finalVerdictReports.forEach((r) => {
    if (r.asset) {
      coveredSuspectAssetMappingIds.add(r.asset.id as string);
    }
  });

  const coveredCount = assets.filter(
    (mapping) => mapping.id && coveredSuspectAssetMappingIds.has(mapping.id),
  ).length;

  const allDone = assets.length > 0 && coveredCount === assets.length;

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddFinalReport = async () => {
    // ---- basic validation ----
    if (!formData.prohibition_id) {
      showTooltip(
        t("please_select_prohibition_record", {
          defaultValue: "Please select a prohibition record",
        }),
        "error",
      );
      return;
    }

    if (!formData.court_decision_summary.trim()) {
      showTooltip(
        t("court_decision_summary_is_required", {
          defaultValue: "Court decision summary is required",
        }),
        "error",
      );
      return;
    }
    if (!formData.court_decision_date) {
      showTooltip(
        t("court_decision_date_is_required", {
          defaultValue: "Court decision date is required",
        }),
        "error",
      );
      return;
    }
    if (assetsNeedingReport?.length > 0 && !formData.suspect_asset_mapping) {
      showTooltip(
        t("please_select_suspect_asset_mapping", {
          defaultValue: "Please select a suspect-asset mapping",
        }),
        "error",
      );
      return;
    }

    const data = new FormData();
    data.append("prohabition_request", formData.prohibition_id);
    data.append("referral", referralId as string);
    data.append("court_decision_date", formData.court_decision_date);
    data.append("court_decision_summary", formData.court_decision_summary);
    data.append(
      "prosecutor_feedback_on_prohabition_decision",
      formData.prosecutor_feedback_on_prohabition_decision || "",
    );
    data.append("is_next_appointment_reason_final_verdict", "true");

    // Append the suspect_asset_mapping ID
    if (formData.suspect_asset_mapping) {
      data.append("count_suspect_asset", formData.suspect_asset_mapping);
    }

    uploadedFiles.forEach((f) => data.append("court_decision_documents", f));

    try {
      setSubmitting(true);

      // Add the report
      const savedReport = await addReport(data);

      // Check if this is the last report needed for this prohibition
      const reportsForThisProhibition = reports.filter(
        (r) =>
          r.prohabition_request === formData.prohibition_id &&
          r.is_next_appointment_reason_final_verdict,
      );

      const isLastReportNeeded = assetsNeedingReport.length <= 1;

      // Also check if all suspect-asset mappings are now covered
      const totalMappings = assets.length;
      const willBeAllCoveredAfterThis =
        coveredCount + (formData.suspect_asset_mapping ? 1 : 0) >=
          totalMappings || assetsNeedingReport.length === 0;

      // If this is the final report for this prohibition, update its status
      if (isLastReportNeeded || willBeAllCoveredAfterThis) {
        // Update the specific prohibition record status to FINAL_VERDICT
        const statusUpdateData = new FormData();
        statusUpdateData.append(
          "status",
          AssetReferralActionStatusEnum?.PROHIBITION_FINAL_VERDICT,
        );

        const updatedProhibition = await updateAssetReferralProhibitionRequest(
          formData.prohibition_id,
          statusUpdateData,
        );

        if (updatedProhibition) {
          showTooltip(
            t("final_report_submitted_and_status_updated", {
              defaultValue:
                "Final report submitted and prohibition status updated to Final Verdict!",
            }),
            "success",
          );

          // Refresh the parent data
          if (onNewDataAdded) {
            const response = await fetchAssetReferralById(referralId as string);
            onNewDataAdded(response);
          }

          setActiveTab?.("Prohibition Reports");
        }
      } else {
        showTooltip(
          t("final_report_submitted_successfully", {
            defaultValue: "Final report submitted successfully",
          }),
          "success",
        );
      }

      // Reset form
      setFormData({
        prohibition_id: "",
        court_decision_date: "",
        court_decision_summary: "",
        prosecutor_feedback_on_prohabition_decision: "",
        suspect_asset_mapping: "",
      });
      setUploadedFiles([]);
    } catch (err) {
      console.error(err);
      showTooltip(
        t("failed_to_submit_final_report", {
          defaultValue: "Failed to submit final report",
        }),
        "error",
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Helper function to get display text for a suspect-asset mapping

  return (
    <div className="p-6">
      <p className="text-sm mb-2 text-gray-400">Fill Final Report</p>

      {/* Show message if no eligible prohibition records */}
      {eligibleProhibitionRecords.length === 0 && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
          <p className="text-sm text-yellow-700">
            No prohibition records in litigation status. Final reports can only
            be added to prohibitions that are in litigation.
          </p>
        </div>
      )}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleAddFinalReport();
        }}
      >
        {/* Prohibition Selection Dropdown */}
        <div className="flex flex-col mb-4">
          <label className="referral-popup-label">
            Select Prohibition Record <span className="text-red-500">*</span>
          </label>
          <select
            className="referral-form-field"
            name="prohibition_id"
            value={formData.prohibition_id}
            onChange={handleInputChange}
            required
            disabled={eligibleProhibitionRecords.length === 0}
          >
            <option value="" disabled>
              {eligibleProhibitionRecords.length === 0
                ? "No prohibitions in litigation"
                : "Select a prohibition record"}
            </option>
            {eligibleProhibitionRecords.map((record) => (
              <option value={record.id} key={record.id}>
                {record.prohabition_request_registration_number ||
                  `Prohibition #${record.id.slice(-6)}`}{" "}
                -
                {record.prohabition_request_summary?.substring(0, 50) ||
                  "No summary"}
                {record.prohabition_court_name
                  ? ` (${record.prohabition_court_name})`
                  : ""}
              </option>
            ))}
          </select>
        </div>

        {assetsNeedingReport.length > 0 ? (
          <div className="flex flex-col mb-4">
            <label className="referral-form-label">
              Select Suspect-Asset Mapping
            </label>
            <select
              className="referral-form-field"
              name="suspect_asset_mapping"
              value={formData.suspect_asset_mapping}
              onChange={handleInputChange}
              disabled={!formData.prohibition_id}
            >
              <option value="">Select Mapping</option>
              {assetsNeedingReport.map((mapping) => (
                <option key={mapping.id} value={mapping.id}>
                  {getMappingDisplayText(mapping)}
                </option>
              ))}
            </select>
          </div>
        ) : (
          <div className="mb-4 p-3 bg-green-50 text-green-700 rounded">
            ✅ All suspect-asset mappings have final reports.
          </div>
        )}

        {/* ---------- Court Decision Summary ---------- */}
        <div className="flex flex-col mb-4">
          <label className="referral-popup-label">Court Decision Summary</label>
          <textarea
            className="referral-form-field min-h-[100px]"
            name="court_decision_summary"
            placeholder="detail here..."
            value={formData.court_decision_summary}
            onChange={handleInputChange}
            disabled={!formData.prohibition_id}
          />
        </div>

        {/* ---------- Court Decision Date ---------- */}
        <div className="flex flex-col mb-4">
          <label className="referral-popup-label">Court Decision Date</label>
          <input
            type="date"
            className="referral-form-field"
            name="court_decision_date"
            value={formData.court_decision_date}
            onChange={handleInputChange}
            disabled={!formData.prohibition_id}
          />
        </div>

        {/* ---------- File upload ---------- */}
        <div className="flex flex-col mb-4">
          <MultipleFileUploadComponent
            onFilesChange={setUploadedFiles}
            uploadedFiles={uploadedFiles}
            label="Report Doc"
          />
        </div>

        {/* ---------- Prosecutor Feedback ---------- */}
        <div className="flex flex-col mb-6">
          <label className="referral-popup-label">Prosecutor Feedback</label>
          <textarea
            className="referral-form-field min-h-[100px]"
            name="prosecutor_feedback_on_prohabition_decision"
            placeholder="Enter prosecutor feedback..."
            value={formData.prosecutor_feedback_on_prohabition_decision}
            onChange={handleInputChange}
            disabled={!formData.prohibition_id}
          />
        </div>

        {/* ---------- Submit button ---------- */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={
              submitting ||
              assetsNeedingReport.length === 0 ||
              !formData.suspect_asset_mapping ||
              !formData.court_decision_summary.trim() ||
              !formData.court_decision_date ||
              !formData.prohibition_id
            }
            className="referral-form-button text-white px-8 py-3 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting
              ? "Submitting..."
              : assetsNeedingReport.length === 0
                ? "No Mappings Left"
                : "Set Court Decision"}
          </button>
        </div>
      </form>
    </div>
  );
};
